<template>
    <default-layout>
        <div class="row">
            <div class="col-12">

                <div class="btn-toolbar float-right" role="toolbar" aria-label="Orders toolbar">
                    <div class="btn-group mr-2" role="group" aria-label="Orders toolbar, first group">
                        <button type="button" class="btn-sm page-link mr-2" @click="onRefresh" title="Refresh">
                            <font-awesome-icon class="mr-1" icon="sync"/>
                            Refresh
                        </button>
                        <button type="button" class="btn-sm page-link" @click="exportCsv" title="Export CSV">
                            <font-awesome-icon class="mr-1" icon="file-csv"/>
                            Export CSV
                        </button>
                    </div>
                </div>

                <h1 class="h4 mb-4 clearfix">
                    Orders
                    <span class="badge badge-pill badge-primary" v-if="orders.total">{{ orders.total }}</span>
                </h1>

            </div>
        </div>

        <div class="row" v-if="cliffMarketModule.status.listOrdersComplete === false">
            <div class="col-12">
                <spinner-component class="p-5" type="spinner-grow"/>
            </div>
        </div>

        <div class="row" v-if="cliffMarketModule.status.listOrdersComplete === true">

            <div class="col-12">
                <form class="form-inline mb-4" @submit.prevent="onRefresh">
                    <div class="form-group mr-2">
                        <input class="form-control form-control-sm" type="text" placeholder="Search..." v-model="term">
                    </div>
                    <button type="submit" class="btn btn-sm btn-primary">Search</button>
                </form>
            </div>

            <div class="col-12 clearfix">
                <pagination-component :page="page" :limit="limit" :total="orders.total" @paginate="onChangePage"/>

                <div class="btn-toolbar float-right" role="toolbar" aria-label="Orders table per page">
                    <div class="btn-group mr-2" role="group" aria-label="Orders toolbar, first group">
                        <form class="form-inline mb-4" @change="onRefresh">
                            <div class="form-group">
                                <label class="mr-2" for="orders-per-page">
                                    <small>Orders per page</small>
                                </label>
                                <select id="orders-per-page" class="form-control form-control-sm" v-model="limit">
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="75">75</option>
                                    <option value="100">100</option>
                                    <option value="500">500</option>
                                </select>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div class="col-12">
                <div class="table-responsive">
                    <table id="orders" class="table">
                        <thead>
                        <tr>
                            <th>Order #</th>
                            <th>Date</th>
                            <th>Customer name</th>
                            <th>Status</th>
                            <th>Total</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>

                        <tr v-for="order of orders.list" :key="order.id">
                            <td class="align-middle">
                                <strong>{{ order.orderNumber }}</strong>
                            </td>
                            <td class="align-middle">
                                <span>{{ moment(order.orderDate).format('DD.MM.YYYY') }}</span>
                            </td>
                            <td class="align-middle">
                                <v-gravatar class="mr-2 rounded-circle" :size="20" :email="order.orderCustomer.email"/>
                                {{ order.orderCustomer.firstName + ' ' + order.orderCustomer.lastName }}
                            </td>
                            <td class="align-middle">
                                <span class="badge" :class="getOrderStatusBadgeClass(order.transactions[0].stateMachineState.technicalName)">
                                    {{ order.transactions[0].stateMachineState.name }}
                                </span>
                            </td>
                            <td class="align-middle">{{ order.price.totalPrice | currency }}</td>
                            <td>
                                <button class="btn btn-sm btn-outline-primary mr-1"
                                        title="View order details"
                                        data-toggle="modal"
                                        data-target="#order-details-modal"
                                        @click="onOpenOrder(order.orderNumber)">
                                    <font-awesome-icon icon="eye"/>
                                </button>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="col-12 clearfix">
                <pagination-component :page="page" :limit="limit" :total="orders.total" @paginate="onChangePage"/>
            </div>
        </div>

        <modal-component id="order-details-modal"
                         class="fade"
                         :title="'Order #' + order.orderNumber"
                         size="modal-lg">
            <div class="modal-body">
                <spinner-component class="p-5" type="spinner-grow" v-if="Object.keys(order).length === 0 || showModalSpinner === true"/>

                <ul class="nav nav-tabs mb-4" id="orderModal" role="tablist" v-if="Object.keys(order).length !== 0 && showModalSpinner === false">
                    <li class="nav-item">
                        <a class="nav-link active" id="details-tab" data-toggle="tab" href="#details" role="tab" aria-controls="details" aria-selected="true">
                            Details
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link" id="licenses-tab" data-toggle="tab" href="#licenses" role="tab" aria-controls="licenses" aria-selected="false">
                            Licenses
                            <span class="badge badge-pill badge-primary" v-if="order.licenses">{{ order.licenses.length }}</span>
                        </a>
                    </li>
                </ul>

                <div class="tab-content mb-4" id="orderModalContent" v-if="Object.keys(order).length !== 0 && showModalSpinner === false">

                    <!-- Details tab pane -->
                    <div class="tab-pane fade show active" id="details" role="tabpanel" aria-labelledby="details-tab">
                        <div class="container">
                            <div class="row mb-4">
                                <div class="col-12">
                                    <h4 class="h6">Customer</h4>
                                    <hr>
                                </div>
                                <div class="col-12">
                                    <div class="media mb-1 d-flex align-items-center">
                                        <v-gravatar class="rounded-circle mr-3" id="gravatar" :size="72" :email="order.orderCustomer.email"/>
                                        <div class="media-body">
                                            <h5 class="m-0 font-weight-bold">{{ order.orderCustomer.firstName + ' ' + order.orderCustomer.lastName }}</h5>
                                            <p class="text-muted mb-0"><a :href="'mailto:' + order.orderCustomer.email">{{ order.orderCustomer.email }}</a></p>
                                            <p class="text-muted mb-0">{{ order.orderCustomer.customerNumber }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row mb-4">
                                <div class="col-4">
                                    <h3 class="h6">Order payment status</h3>
                                    <hr>
                                    <button v-if="order.transactions[0].stateMachineState.technicalName !== 'open'"
                                            :class="getOrderPaymentStatusClass(order.transactions[0].stateMachineState.technicalName)"
                                            class="btn btn-outline-success btn-block"
                                            disabled="disabled">
                                        {{ order.transactions[0].stateMachineState.name }}
                                    </button>
                                    <form @submit.prevent="onUpdateOrderPaymentStatus($event)" v-if="order.transactions[0].stateMachineState.technicalName === 'open'">
                                        <select class="form-control form-control-sm mb-2" name="payment-status">
                                            <option value="cancel">Cancelled</option>
                                            <option value="pay">Paid</option>
                                        </select>
                                        <button type="submit" class="btn btn-sm btn-outline-primary btn-block">Update Payment Status</button>
                                    </form>
                                </div>
                            </div>

                            <div class="row mb-4">
                                <div class="col-12">
                                    <h4 class="h6">Line items</h4>
                                    <hr>
                                </div>
                                <div class="col-12">
                                    <table class="table table-borderless table-striped">
                                        <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Quantity</th>
                                            <th>Price per unit</th>
                                            <th>Subtotal</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="lineItem in order.lineItems" :key="lineItem.id">
                                            <td>{{ lineItem.label }}</td>
                                            <td>{{ lineItem.quantity }}</td>
                                            <td>{{ lineItem.unitPrice | currency }}</td>
                                            <td>{{ lineItem.totalPrice | currency }}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12">
                                    <h4 class="h6">Details</h4>
                                    <hr>
                                </div>

                                <div class="col-6">
                                    <table class="order-details-table table table-borderless text-left mb-0">
                                        <tbody>
                                        <tr>
                                            <td><b>Created at</b></td>
                                            <td>{{ moment(order.orderDate).format('DD.MM.YYYY') }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>Order number</b></td>
                                            <td>{{ order.orderNumber }}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div class="col-6">
                                    <table class="order-details-table table table-borderless text-right mb-0">
                                        <tr>
                                            <td><b>Shipping costs</b></td>
                                            <td>{{ order.shippingTotal | currency }}</td>
                                        </tr>
                                        <tr>
                                            <td><b>Total ({{ order.taxStatus }})</b></td>
                                            <td>{{ order.amountTotal | currency }}</td>
                                        </tr>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Licenses tab pane -->
                    <div class="tab-pane fade" id="licenses" role="tabpanel" aria-labelledby="licenses-tab">

                        <!-- MODAL SPINNER -->
                        <div class="row mb-3 justify-content-center" v-if="showModalSpinner === true">
                            <spinner-component class="p-5" type="spinner-grow"/>
                        </div>

                        <!-- MODAL CONTENT -->
                        <div class="row mb-3" v-if="showModalSpinner === false">

                            <!-- ORDER LICENSES TABLE -->
                            <div class="col-12" v-if="order.licenses.length > 0">
                                <div class="table-responsive">
                                    <table class="table">
                                        <thead>
                                        <th scope="col">License Code</th>
                                        <th scope="col">Type</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Actions</th>
                                        </thead>
                                        <tbody>
                                        <tr v-for="license in order.licenses" :key="license.id">
                                            <td class="align-middle">{{ license.licenseCode }}</td>
                                            <td>
                                                <span class="badge badge-primary" v-if="license.type === 'LICENSE_RESELLER'">
                                                    Reseller
                                                </span>
                                                <span class="badge badge-light" v-if="license.type === 'LICENSE_DEFAULT'">
                                                    Default
                                                </span>
                                            </td>
                                            <td>
                                                <span v-if="license.status === 'LICENSE_USED'" class="badge badge-warning">
                                                    Used
                                                </span>
                                                <span v-else-if="license.status === 'LICENSE_ASSIGNED'" class="badge badge-success">
                                                    Assigned
                                                </span>
                                                <span v-else-if="license.status === 'LICENSE_UNASSIGNED'" class="badge badge-light">
                                                    Unassigned
                                                </span>
                                                <span v-else-if="license.status === 'LICENSE_CANCELLED'" class="badge badge-danger">
                                                    Cancelled
                                                </span>
                                                <span v-else-if="license.status === 'LICENSE_ACTIVE'" class="badge badge-info">
                                                    Active
                                                </span>
                                                <span v-else-if="license.status === 'LICENSE_WATCHING'" class="badge badge-dark">
                                                    <font-awesome-icon class="mr-1 small fa-blink" icon="play"/>
                                                    Watching
                                                </span>
                                                <span v-else class="badge badge-secondary">
                                                    {{ license.status }}
                                                </span>
                                            </td>
                                            <td>
                                                <button class="btn btn-sm btn-outline-danger mr-2" @click="onDeleteLicense(license.id)" title="Delete license">
                                                    <font-awesome-icon icon="trash"/>
                                                </button>

                                                <form class="form-inline d-inline-block mr-2"
                                                      v-if="license.type === 'LICENSE_DEFAULT' && license.status !== 'LICENSE_ASSIGNED'"
                                                      @submit.prevent="onUpdateLicense($event, license.id)">
                                                    <select class="mr-2 form-control form-control-sm" name="license-status" required="required">
                                                        <option value="">-- Status --</option>
                                                        <option value="LICENSE_ACTIVE" v-if="license.status !== 'LICENSE_ACTIVE'">
                                                            Active
                                                        </option>
                                                        <option value="LICENSE_ASSIGNED" v-if="license.status !== 'LICENSE_ASSIGNED'">
                                                            Assigned
                                                        </option>
                                                    </select>
                                                    <button class="btn btn-sm btn-outline-secondary" type="submit" title="Reset license">
                                                        <font-awesome-icon icon="history"/>
                                                    </button>
                                                </form>

                                                <a class="btn btn-sm btn-outline-info"
                                                   title="License details"
                                                   :href="'/licenses/' + license.id"
                                                   target="_blank"
                                                   rel="noopener noreferrer"
                                                   v-if="license.type === 'LICENSE_DEFAULT' && (license.status === 'LICENSE_WATCHING' || license.status === 'LICENSE_USED')">
                                                    <font-awesome-icon icon="info-circle" class="mr-1"/>
                                                    Details
                                                </a>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- NO ORDER LICENSES ALERT -->
                            <div class="col-12 mb-3" v-if="order.licenses.length === 0">
                                <div class="alert alert-info" role="alert">
                                    This order does not have any licenses.
                                </div>
                            </div>

                            <!-- CREATE LICENSES -->
                            <div class="col-12">
                                <form class="form-inline" @submit.prevent="onCreateLicense()">
                                    <label class="sr-only" for="create-order-license-amount">Amount</label>
                                    <div class="input-group input-group-sm mr-sm-2">
                                        <div class="input-group-prepend">
                                            <div class="input-group-text">Amount</div>
                                        </div>
                                        <input class="form-control form-control-sm" id="create-order-license-amount" type="text" required="required" v-model="createLicenseAmount">
                                    </div>

                                    <label class="sr-only" for="create-order-license-product">Product</label>
                                    <div class="input-group input-group-sm mr-sm-2">
                                        <div class="input-group-prepend">
                                            <div class="input-group-text">Product</div>
                                        </div>
                                        <select class="form-control form-control-sm" id="create-order-license-product" v-model="createLicenseProduct">
                                            <option v-for="product in order.lineItems" :value="product.id" :key="product.id">
                                                {{ product.label }}
                                            </option>
                                        </select>
                                    </div>

                                    <button class="btn btn-sm btn-primary" type="submit">Create</button>
                                </form>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </modal-component>
    </default-layout>
</template>

<script>
    import moment from "moment";
    import DefaultLayout from "../components/layout/DefaultLayout";
    import SpinnerComponent from "../components/ui/SpinnerComponent";
    import ModalComponent from "../components/ui/ModalComponent";
    import PaginationComponent from "../components/PaginationComponent";

    export default {
        name: "OrdersPage",
        components: { PaginationComponent, ModalComponent, SpinnerComponent, DefaultLayout },
        computed: {
            cliffMarketModule() {
                return this.$store.state.cliffMarket;
            },
            customer() {
                return this.$store.state.cliffMarket.customer;
            },
            order() {
                return this.$store.state.cliffMarket.order;
            },
            orders() {
                return this.$store.state.cliffMarket.orders;
            }
        },
        data() {
            return {
                page: 1,
                limit: 10,
                sort: [
                    {
                        field: 'orderDateTime',
                        naturalSorting: false,
                        order: 'DESC'
                    }
                ],
                term: undefined,
                showModalSpinner: false,
                createLicenseAmount: 1,
                createLicenseProduct: null
            }
        },
        methods: {
            moment(date) {
                return moment(date);
            },
            async onOpenOrder(orderNumber) {
                this.showModalSpinner = true;
                await this.$store.dispatch('cliffMarket/getOrder', orderNumber);
                await this.$store.dispatch('cliffMarket/getCustomer', this.order.party);
                this.showModalSpinner = false;
            },
            onRefresh() {
                this.$store.dispatch('cliffMarket/clearOrders');
                this.$store.dispatch('cliffMarket/listOrders', {
                    page: this.page,
                    limit: this.limit,
                    sort: this.sort,
                    term: this.term
                });
            },
            onChangePage(page) {
                if (page <= 0) {
                    return;
                }

                this.page = parseInt(page);
                this.onRefresh();
            },
            async onCreateLicense() {
                const orderNumber = this.order.orderNumber;
                this.showModalSpinner = true;

                await this.$store.dispatch('keyNave/createLicense', {
                    amount: this.createLicenseAmount,
                    party: this.customer.id,
                    order: this.order.id,
                    product: this.createLicenseProduct,
                    purchaseDate: this.order.orderDateTime
                });

                await this.$store.dispatch('cliffMarket/getOrder', orderNumber);

                this.showModalSpinner = false;
                this.$forceUpdate();
            },
            async onUpdateLicense(event, licenseId) {
                const orderNumber = this.order.orderNumber;
                this.showModalSpinner = true;

                await this.$store.dispatch('keyNave/updateLicense', {
                    license: licenseId,
                    status:  event.target.elements['license-status'].value
                });

                await this.$store.dispatch('cliffMarket/getOrder', orderNumber);

                this.showModalSpinner = false;
                this.$forceUpdate();
            },
            async onUpdateOrderPaymentStatus(event) {
                this.showModalSpinner = true;

                await this.$store.dispatch('cliffMarket/updatePaymentState', {
                    order: this.order.id,
                    state: event.target.elements['payment-status'].value
                });
                await this.$store.dispatch('cliffMarket/getOrder', this.order.orderNumber);
                await this.$store.dispatch('cliffMarket/clearListOrders');
                await this.$store.dispatch('cliffMarket/listOrders', {
                    page: this.page,
                    limit: this.limit,
                    sort: this.sort,
                    term: this.term
                });

                this.showModalSpinner = false;
                this.$forceUpdate();
            },
            async onDeleteLicense(licenseId) {
                const orderNumber = this.order.orderNumber;
                this.showModalSpinner = true;

                await this.$store.dispatch('keyNave/deleteLicense', licenseId);
                await this.$store.dispatch('cliffMarket/getOrder', orderNumber);

                this.showModalSpinner = false;
                this.$forceUpdate();
            },
            exportCsv() {
                let exportData = [];
                let csvContent = "data:text/csv;charset=utf-8,";

                this.orders.list.forEach(order => {
                    exportData.push({
                        ORDER_ID: order.id,
                        ORDER_NUMBER: order.orderNumber,
                        ORDER_DATE: order.orderDate,
                        ORDER_DATE_TIME: order.orderDateTime,
                        ORDER_AMOUNT_NET: order.amountNet,
                        ORDER_AMOUNT_TOTAL: order.amountTotal,
                        ORDER_POSITION_PRICE: order.positionPrice,
                        ORDER_SHIPPING_TOTAL: order.shippingTotal,
                        //ORDER_STATUS: order.stateMachineState.name,
                        ORDER_PAYMENT_STATUS: order.transactions[0].stateMachineState.name,
                        //ORDER_DELIVERY_STATUS: order.deliveries[0].stateMachineState.name,
                        ORDER_CREATED_AT: order.createdAt,
                        ORDER_UPDATED_AT: order.updatedAt,
                        CUSTOMER_ID: order.orderCustomer.id,
                        CUSTOMER_NUMBER: order.orderCustomer.customerNumber,
                        CUSTOMER_EMAIL: order.orderCustomer.email,
                        CUSTOMER_SALUTATION: order.orderCustomer.salutation,
                        CUSTOMER_FIRST_NAME:  order.orderCustomer.firstName,
                        CUSTOMER_LAST_NAME:  order.orderCustomer.lastName,
                        CUSTOMER_COMPANY: order.orderCustomer.company,
                        CUSTOMER_IP_ADDRESS: order.orderCustomer.remoteAddress,
                        CUSTOMER_CREATED_AT: order.orderCustomer.createdAt,
                        CUSTOMER_UPDATED_AT: order.orderCustomer.updatedAt,
                    });
                });

                csvContent += [
                    Object.keys(exportData[0]).join(";"),
                    ...exportData.map(order => Object.values(order).join(";"))
                ]
                    .join("\n")
                    .replace(/(^\[)|(\]$)/gm, "");

                const data = encodeURI(csvContent);
                const link = document.createElement("a");
                link.setAttribute("href", data);
                link.setAttribute("download", "interactive-link-export-orders-" + moment().format('YYYYMMDD-HHMMSS') + ".csv");
                link.click();
            },
            getOrderStatusBadgeClass(technicalName) {
                return {
                    'badge-success': technicalName === 'paid' || technicalName === 'paid_partially',
                    'badge-info': technicalName === 'open',
                    'badge-danger': technicalName === 'cancelled',
                    'badge-warning': technicalName === 'refunded' || technicalName === 'refunded_partially'
                };
            },
            getOrderPaymentStatusClass(paymentStatus) {
                return {
                    'btn-outline-success': paymentStatus === 'paid',
                    'btn-outline-danger': paymentStatus === 'cancelled'
                }
            }
        },
        beforeMount() {
            if (Object.prototype.hasOwnProperty.call(this.$route.query, 'page')) {
                const page = parseInt(this.$route.query.page);
                this.page = page ? page : 1;
            }

            if (Object.prototype.hasOwnProperty.call(this.$route.query, 'limit')) {
                const limit = parseInt(this.$route.query.limit);
                this.limit = limit ? limit : 10;
            }

            if (this.$store.state.cliffMarket.status.listOrdersComplete === false) {
                this.$store.dispatch('cliffMarket/listOrders', {
                    page: this.page,
                    limit: this.limit,
                    sort: this.sort,
                    term: this.term
                });
            }
        }
    }
</script>

<style scoped>

</style>